import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { AlertTriangle, TrendingUp, TrendingDown, Target, Info } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, AreaChart, Area, XAxis, YAxis, CartesianGrid, ReferenceLine, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtCur = (v) => v == null ? '-' : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
const fmtPct = (v) => v == null ? '-' : `${v.toFixed(1)}%`;

const PIE_COLORS = ['#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6', '#10b981', '#ec4899', '#06b6d4', '#64748b'];

const CostTrendChart = ({ subcat, year }) => {
  const data = subcat.trend.filter(t => t.value != null);
  if (data.length < 2) return null;

  const idealPct = subcat.ideal?.ideal_pct;
  const maxPct = subcat.ideal?.max_pct;
  const benchAvg = subcat.benchmark?.average;

  return (
    <div className="mt-3">
      <div className="flex items-center gap-3 mb-2 text-[11px]">
        <span className="font-medium text-slate-600">Andamento 12 mesi (% su fatturato)</span>
        {benchAvg != null && <span className="text-blue-500 flex items-center gap-0.5"><span className="inline-block w-4 h-0 border-t-2 border-blue-400 border-dashed" /> Media settore {benchAvg}%</span>}
        {idealPct != null && <span className="text-emerald-500 flex items-center gap-0.5"><span className="inline-block w-4 h-0 border-t-2 border-emerald-400 border-dashed" /> Ideale {idealPct}%</span>}
        {maxPct != null && <span className="text-red-400 flex items-center gap-0.5"><span className="inline-block w-4 h-0 border-t-2 border-red-300 border-dashed" /> Max {maxPct}%</span>}
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={data} margin={{ top: 5, right: 10, bottom: 0, left: -15 }}>
          <defs>
            <linearGradient id={`grad-${subcat.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ef4444" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#ef4444" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="month_name" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} unit="%" domain={[0, 'auto']} />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
            formatter={(v, name) => name === 'pct' ? [`${v}%`, '% su fatturato'] : [fmtCur(v), 'Valore']}
          />
          {benchAvg != null && <ReferenceLine y={benchAvg} stroke="#3b82f6" strokeDasharray="4 4" strokeWidth={1.5} label={{ value: `Media ${benchAvg}%`, fontSize: 9, fill: '#3b82f6', position: 'right' }} />}
          {idealPct != null && <ReferenceLine y={idealPct} stroke="#10b981" strokeDasharray="4 4" strokeWidth={1.5} label={{ value: `Ideale ${idealPct}%`, fontSize: 9, fill: '#10b981', position: 'right' }} />}
          {maxPct != null && <ReferenceLine y={maxPct} stroke="#ef4444" strokeDasharray="4 4" strokeWidth={1} label={{ value: `Max ${maxPct}%`, fontSize: 9, fill: '#ef4444', position: 'right' }} />}
          <Area type="monotone" dataKey="pct" stroke="#ef4444" fill={`url(#grad-${subcat.key})`} strokeWidth={2.5}
            dot={{ r: 4, fill: '#ef4444', stroke: '#fff', strokeWidth: 2 }}
            activeDot={{ r: 6, fill: '#ef4444' }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const CostSubcatCard = ({ subcat, year }) => {
  const isAboveThreshold = subcat.exceeds_threshold;
  const isAboveIdealMax = subcat.ideal?.max_pct && subcat.ytd_pct > subcat.ideal.max_pct;
  const isAboveBenchmark = subcat.benchmark?.average && subcat.ytd_pct > subcat.benchmark.average;
  const prevDiff = subcat.prev_year?.avg_pct ? subcat.avg_pct - subcat.prev_year.avg_pct : null;
  const prevBetter = prevDiff != null ? prevDiff < 0 : null;

  const borderColor = isAboveIdealMax ? 'border-red-300 bg-red-50/30' :
    isAboveBenchmark ? 'border-amber-300 bg-amber-50/30' :
    isAboveThreshold ? 'border-slate-300' : 'border-slate-200';

  return (
    <Card className={`${borderColor} border-2`} data-testid={`cost-detail-${subcat.key}`}>
      <CardContent className="pt-4 pb-3">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="font-bold text-sm">{subcat.label}</h4>
            <div className="flex items-center gap-2 mt-1">
              {isAboveIdealMax && <Badge variant="destructive" className="text-[10px]">Sopra il massimo</Badge>}
              {isAboveBenchmark && !isAboveIdealMax && <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">Sopra media settore</Badge>}
              {isAboveThreshold && <Badge variant="secondary" className="text-[10px]">&gt;10% del fatturato</Badge>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-black tabular-nums">{fmtPct(subcat.ytd_pct)}</div>
            <div className="text-xs text-muted-foreground">del fatturato</div>
          </div>
        </div>

        {/* Values row */}
        <div className="grid grid-cols-3 gap-3 bg-white/60 rounded-lg p-2.5 border border-slate-200/50 mb-3 text-center">
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Media/mese</div>
            <div className="text-sm font-bold tabular-nums">{fmtCur(subcat.avg_monthly)}</div>
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Tot. YTD</div>
            <div className="text-sm font-bold tabular-nums">{fmtCur(subcat.ytd_total)}</div>
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider">vs {year - 1}</div>
            {prevDiff != null ? (
              <div className={`text-sm font-bold tabular-nums flex items-center justify-center gap-0.5 ${prevBetter ? 'text-emerald-600' : 'text-red-500'}`}>
                {prevBetter ? <TrendingDown className="h-3.5 w-3.5" /> : <TrendingUp className="h-3.5 w-3.5" />}
                {prevDiff > 0 ? '+' : ''}{prevDiff.toFixed(1)} pp
              </div>
            ) : <div className="text-sm text-slate-300">-</div>}
          </div>
        </div>

        {/* Benchmark & ideal comparison */}
        {(subcat.benchmark || subcat.ideal) && (
          <div className="space-y-1.5 mb-3">
            {subcat.benchmark?.average != null && (
              <div className="flex items-center justify-between text-[12px] px-2 py-1 bg-blue-50 rounded">
                <span className="text-blue-700">Media settore</span>
                <span className="font-bold text-blue-800">{subcat.benchmark.average}%</span>
              </div>
            )}
            {subcat.benchmark?.best != null && (
              <div className="flex items-center justify-between text-[12px] px-2 py-1 bg-emerald-50 rounded">
                <span className="text-emerald-700">Migliori del settore</span>
                <span className="font-bold text-emerald-800">{subcat.benchmark.best}%</span>
              </div>
            )}
            {subcat.ideal?.ideal_pct != null && (
              <div className="flex items-center justify-between text-[12px] px-2 py-1 bg-slate-50 rounded">
                <span className="text-slate-600 flex items-center gap-1"><Target className="h-3 w-3" /> Valore ideale</span>
                <span className="font-bold text-slate-700">{subcat.ideal.ideal_pct}%</span>
              </div>
            )}
          </div>
        )}

        {/* Advice */}
        {subcat.ideal?.advice && isAboveThreshold && (
          <div className={`p-2.5 rounded-lg border flex gap-2 mb-2 ${isAboveIdealMax ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
            <Info className={`h-4 w-4 flex-shrink-0 mt-0.5 ${isAboveIdealMax ? 'text-red-500' : 'text-amber-500'}`} />
            <p className="text-[12px] leading-relaxed text-slate-700">{subcat.ideal.advice}</p>
          </div>
        )}

        {/* Trend chart for items > 10% */}
        {isAboveThreshold && <CostTrendChart subcat={subcat} year={year} />}
      </CardContent>
    </Card>
  );
};

const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, name, pct }) => {
  if (pct < 3) return null;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 22;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#334155" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={11} fontWeight={600}>
      {name} {pct.toFixed(0)}%
    </text>
  );
};

export const CostsDetailSection = ({ clientId, year }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/clients/${clientId}/costs-detail`, { params: { year } });
        setData(res.data);
      } catch {
        setData(null);
      } finally { setLoading(false); }
    };
    if (clientId) fetch();
  }, [clientId, year]);

  if (loading) return <div className="py-12 text-center text-muted-foreground">Caricamento dettaglio costi...</div>;
  if (!data || data.subcategories.length === 0) return (
    <Card><CardContent className="py-12 text-center">
      <p className="text-lg font-semibold mb-2">Nessun dettaglio costi disponibile</p>
      <p className="text-muted-foreground">Configura le sottocategorie dei costi fissi nella sezione "Configura Conti"</p>
    </CardContent></Card>
  );

  const { pie_chart, subcategories, ytd_revenue, ytd_fixed_costs, ytd_fixed_costs_pct, ytd_variable_costs, ytd_variable_costs_pct, months_with_data } = data;
  const heavyCosts = subcategories.filter(s => s.exceeds_threshold);
  const lightCosts = subcategories.filter(s => !s.exceeds_threshold);

  return (
    <div className="space-y-6" data-testid="costs-detail-section">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-2">
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Costi Fissi YTD</div>
            <div className="text-xl font-black tabular-nums">{fmtCur(ytd_fixed_costs)}</div>
            <div className="text-sm text-muted-foreground">{fmtPct(ytd_fixed_costs_pct)} del fatturato</div>
          </CardContent>
        </Card>
        <Card className="border-2">
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Costi Variabili YTD</div>
            <div className="text-xl font-black tabular-nums">{fmtCur(ytd_variable_costs)}</div>
            <div className="text-sm text-muted-foreground">{fmtPct(ytd_variable_costs_pct)} del fatturato</div>
          </CardContent>
        </Card>
        <Card className="border-2">
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Fatturato YTD</div>
            <div className="text-xl font-black tabular-nums">{fmtCur(ytd_revenue)}</div>
            <div className="text-sm text-muted-foreground">{months_with_data} mesi caricati</div>
          </CardContent>
        </Card>
        <Card className="border-2">
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Voci &gt; 10% fatturato</div>
            <div className={`text-xl font-black ${heavyCosts.length > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{heavyCosts.length}</div>
            <div className="text-sm text-muted-foreground">{heavyCosts.length > 0 ? 'Da monitorare' : 'Nessuna critica'}</div>
          </CardContent>
        </Card>
      </div>

      {/* Pie Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Composizione Costi Fissi</CardTitle>
          <CardDescription>Ripartizione per categoria - {months_with_data} mesi {year}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col lg:flex-row items-center gap-6">
            <ResponsiveContainer width="100%" height={300} className="max-w-[400px]">
              <PieChart>
                <Pie
                  data={pie_chart}
                  cx="50%" cy="50%"
                  outerRadius={110}
                  innerRadius={50}
                  dataKey="value"
                  label={CustomPieLabel}
                  labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                  strokeWidth={2}
                  stroke="#fff"
                >
                  {pie_chart.map((entry, i) => (
                    <Cell key={entry.key} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => fmtCur(v)}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-2 w-full">
              {pie_chart.map((entry, i) => (
                <div key={entry.key} className="flex items-center justify-between py-1.5 px-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="text-sm font-medium">{entry.name}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm tabular-nums font-semibold">{fmtCur(entry.value)}</span>
                    <span className="text-sm tabular-nums text-muted-foreground w-12 text-right">{fmtPct(entry.pct)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Heavy costs (>10% of revenue) with trend + benchmark */}
      {heavyCosts.length > 0 && (
        <div>
          <div className="mb-3">
            <h3 className="text-base font-bold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Costi oltre il 10% del fatturato
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">Queste voci pesano di piu' e meritano un'analisi approfondita con trend, benchmark e valori ideali</p>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {heavyCosts.map(s => <CostSubcatCard key={s.key} subcat={s} year={year} />)}
          </div>
        </div>
      )}

      {/* Light costs (<10% of revenue) */}
      {lightCosts.length > 0 && (
        <div>
          <div className="mb-3">
            <h3 className="text-base font-bold">Altre voci di costo fisso</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Sotto il 10% del fatturato — incidenza contenuta</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {lightCosts.map(s => <CostSubcatCard key={s.key} subcat={s} year={year} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default CostsDetailSection;
